import useRequest from "@/lib/request/useRequest";
import { useMemo } from "react";
import { FertilizerProduct, FertilizerType } from "@/types/products";
import {
    Autocomplete,
    AutocompleteSection,
    AutocompleteItem,
} from "@heroui/react";

function ProductAutoComplete({
    selectedProductId,
    setSelectedProductId,
    isDisabled,
}: {
    selectedProductId: string | null;
    setSelectedProductId: (productId: string | null) => void;
    isDisabled?: boolean;
}) {
    const { useGetRequest } = useRequest();
    const getFertilizers = useGetRequest<FertilizerProduct[]>({
        url: `/fertilizers/`,
    });
    const fertilizerOptions = useMemo(() => {
        const dryFertilizers =
            getFertilizers.data
                ?.filter(
                    (fertilizer) =>
                        fertilizer.fertilizer_type === FertilizerType.Dry,
                )
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((fertilizer) => ({
                    value: fertilizer.id,
                    label: fertilizer.name,
                })) ?? [];
        const liquidFertilizers =
            getFertilizers.data
                ?.filter(
                    (fertilizer) =>
                        fertilizer.fertilizer_type === FertilizerType.Liquid,
                )
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((fertilizer) => ({
                    value: fertilizer.id,
                    label: fertilizer.name,
                })) ?? [];
        const pgrProducts =
            getFertilizers.data
                ?.filter(
                    (fertilizer) =>
                        fertilizer.fertilizer_type ===
                        FertilizerType.PlantGrowthRegulator,
                )
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((fertilizer) => ({
                    value: fertilizer.id,
                    label: fertilizer.name,
                })) ?? [];
        const harvestAidProducts =
            getFertilizers.data
                ?.filter(
                    (fertilizer) =>
                        fertilizer.fertilizer_type ===
                        FertilizerType.HarvestAid,
                )
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((fertilizer) => ({
                    value: fertilizer.id,
                    label: fertilizer.name,
                })) ?? [];
        const favoriteProducts =
            getFertilizers.data
                ?.filter((fertilizer) => Boolean(fertilizer.is_favorite))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((fertilizer) => ({
                    value: fertilizer.id,
                    label: fertilizer.name,
                })) ?? [];
        const coreProducts =
            getFertilizers.data
                ?.filter((fertilizer) => Boolean(fertilizer.core_product))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((fertilizer) => ({
                    value: fertilizer.id,
                    label: fertilizer.name,
                })) ?? [];
        return [
            {
                value: "favorite",
                label: "Favorite Products",
                options: favoriteProducts,
            },
            {
                value: "core",
                label: "Core Products",
                options: coreProducts,
            },
            {
                value: FertilizerType.Dry,
                label: "Dry Fertilizer",
                options: dryFertilizers,
            },
            {
                value: FertilizerType.Liquid,
                label: "Liquid Fertilizer",
                options: liquidFertilizers,
            },
            {
                value: FertilizerType.PlantGrowthRegulator,
                label: "Plant Growth Regulator",
                options: pgrProducts,
            },
            {
                value: FertilizerType.HarvestAid,
                label: "Harvest Aid",
                options: harvestAidProducts,
            },
        ];
    }, [getFertilizers.data]);

    const selectedKey = useMemo(() => {
        if (!selectedProductId) return null;
        for (const section of fertilizerOptions) {
            const match = section.options.find(
                (o) => String(o.value) === String(selectedProductId),
            );
            if (match) return `${section.value}-${match.value}`;
        }
        return null;
    }, [selectedProductId, fertilizerOptions]);

    return (
        <Autocomplete
            selectedKey={selectedKey}
            onSelectionChange={(key) => {
                if (key == null) {
                    setSelectedProductId(null);
                    return;
                }
                const strKey = String(key);
                const dashIdx = strKey.indexOf("-");
                const id =
                    dashIdx !== -1 ? strKey.substring(dashIdx + 1) : strKey;
                setSelectedProductId(id);
            }}
            label="Product"
            labelPlacement="outside"
            placeholder="Select Product"
            size="sm"
            isDisabled={isDisabled}
            isLoading={getFertilizers.isLoading}
        >
            {fertilizerOptions.map((section) => (
                <AutocompleteSection
                    key={`section-${section.value}`}
                    title={section.label}
                    className="font-bold"
                >
                    {section.options.map((option) => (
                        <AutocompleteItem
                            key={`${section.value}-${option.value}`}
                            textValue={option.label}
                        >
                            {option.label}
                        </AutocompleteItem>
                    ))}
                </AutocompleteSection>
            ))}
        </Autocomplete>
    );
}

export default ProductAutoComplete;
